import { ROUTE_PATHS } from "@/routes/routePaths";

export interface GuideSection {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
}

export interface GuideArticle {
  slug: string;
  title: string;
  description: string;
  eyebrow: string;
  intro: string;
  sections: GuideSection[];
  relatedLinks: { label: string; path: string }[];
}

export const GUIDE_ARTICLES: Record<string, GuideArticle> = {
  "how-carpooling-works-india": {
    slug: "how-carpooling-works-india",
    title: "How carpooling works in India",
    description:
      "A practical guide to finding rides, offering seats, splitting costs, and travelling safely with verified co-travellers on Indian highways and city roads.",
    eyebrow: "Explainer",
    intro:
      "Carpooling in India is not a new idea — office WhatsApp groups and college ride boards have done it informally for years. Duri makes it structured: verified identities, clear pickup points, upfront seat pricing, and ratings after every trip.",
    sections: [
      {
        heading: "Two roles: guest and host",
        paragraphs: [
          "A guest books a seat in someone else's car heading the same way. A host is the driver who lists empty seats on a route they already plan to drive. Both sides verify with government ID before the first booking.",
        ],
      },
      {
        heading: "How a typical trip works",
        paragraphs: ["The flow is the same for city commutes and intercity highways like NH65."],
        bullets: [
          "Search by origin, destination, date, and direction.",
          "Compare hosts by rating, vehicle type, departure time, and women-only filters if needed.",
          "Book and pay in-app — no cash negotiation at pickup.",
          "Chat in-app for exact pickup pin; share live trip status with family.",
          "Rate the trip after drop-off — ratings keep the network trustworthy.",
        ],
      },
      {
        heading: "How costs are split",
        paragraphs: [
          "Hosts set a per-seat price that covers fuel, tolls, and wear — not profit in the commercial sense. On a 275 km Hyderabad–Vijayawada run, four guests might pay ₹450–₹550 each while the host recovers ₹1,800–₹2,200 in driving costs. That is dramatically less than a solo cab fare.",
        ],
      },
      {
        heading: "City vs intercity carpools",
        paragraphs: [
          "Daily commute carpools are short, repeat trips — same host and guests often travel together every weekday. Intercity carpools are longer, scheduled around weekends, festivals, and hybrid work patterns. Duri supports both with the same verification and safety tools.",
        ],
      },
    ],
    relatedLinks: [
      { label: "How Duri works", path: ROUTE_PATHS.HOW_DURI_WORKS },
      { label: "Find rides", path: ROUTE_PATHS.FIND_RIDES },
      { label: "Safety on Duri", path: ROUTE_PATHS.SAFETY },
    ],
  },
  "ridesharing-legal-safe-india": {
    slug: "ridesharing-legal-safe-india",
    title: "Is ridesharing legal and safe in India?",
    description:
      "Understand how non-commercial ride sharing differs from taxis, what Indian law generally allows, and how Duri keeps carpools safe with verification and in-app protections.",
    eyebrow: "Explainer",
    intro:
      "Ride sharing — sharing a private car's empty seats and splitting fuel costs — is not the same as operating a taxi or cab service. Commercial transport needs permits and fare regulation; private cost-sharing among individuals follows different rules.",
    sections: [
      {
        heading: "Legal context: sharing costs vs commercial rides",
        paragraphs: [
          "Indian motor vehicle law distinguishes commercial passenger services from private vehicles. Carpooling platforms that facilitate cost-sharing among private individuals, without the driver seeking profit as a transport business, operate in a different category from Ola/Uber-style aggregators.",
          "Duri hosts list trips they already intend to take — to office, home, or another city — and recover shared expenses. Guests pay a seat fee aligned with fuel and toll split, not a metered commercial fare.",
        ],
      },
      {
        heading: "What makes a carpool safe in practice",
        paragraphs: ["Legal clarity matters less day-to-day than verifiable trust. Duri's safety stack includes:"],
        bullets: [
          "Aadhaar-based identity verification for hosts and guests.",
          "Vehicle details and seat capacity checked at listing.",
          "Ratings and reviews visible before booking.",
          "In-app chat — no need to share personal phone numbers prematurely.",
          "Live trip tracking and SOS support during the ride.",
          "Women-only and women-preferred ride filters.",
        ],
      },
      {
        heading: "Your checklist before booking",
        paragraphs: [
          "Read recent reviews for the specific route. Confirm pickup pin in chat 30 minutes before departure. Share live trip status with a family member. Avoid cash payments outside the app — in-app booking keeps a record if support is needed.",
        ],
      },
    ],
    relatedLinks: [
      { label: "Duri safety", path: ROUTE_PATHS.SAFETY },
      { label: "FAQs", path: ROUTE_PATHS.FAQS },
      { label: "Verification process", path: ROUTE_PATHS.VERIFICATION_PROCESS },
    ],
  },
  "save-commute-cost-hyderabad": {
    slug: "save-commute-cost-hyderabad",
    title: "How to save daily commute cost in Hyderabad",
    description:
      "Practical ways Hyderabad office commuters cut fuel, toll, and parking spend with daily carpool — routes, timing, and typical monthly savings.",
    eyebrow: "Hyderabad guide",
    intro:
      "Hyderabad commuters on the west corridor (Gachibowli, Hitech City, Financial District) often spend ₹12,000–₹18,000/month on fuel, ORR tolls, and parking if they drive solo. Carpooling the same route with two or three co-travellers can cut that by half or more.",
    sections: [
      {
        heading: "Where carpool saves the most",
        paragraphs: [
          "Highest savings are on ORR-linked commutes where distance and tolls add up: Miyapur → Gachibowli, Madhapur → Financial District, Uppal → Hitech City. Metro helps for some legs, but tech parks south of ORR still depend on road transport.",
        ],
        bullets: [
          "Hitech City ↔ Gachibowli (25–35 min): ₹80–₹120/seat vs ₹350+ cab.",
          "Madhapur ↔ Financial District (30–45 min): parking alone can exceed ₹3,000/month downtown.",
          "LB Nagar ↔ Gachibowli (45–60 min): long solo drives benefit most from toll split.",
        ],
      },
      {
        heading: "Timing that matches office hours",
        paragraphs: [
          "Most Hyderabad office carpools cluster around 8:30–9:30 AM arrival and 6:30–7:30 PM departure. Booking a recurring slot with the same host avoids daily search friction and builds trust between the same four commuters.",
        ],
      },
      {
        heading: "Monthly savings example",
        paragraphs: [
          "A Gachibowli → Financial District commuter driving alone might spend ~₹14,000/month (fuel ₹8,000, toll ₹2,500, parking ₹3,500). As a carpool guest paying ₹100–₹150 per leg, 22 workdays ≈ ₹4,400–₹6,600/month — a ₹7,000–₹9,000 monthly difference.",
        ],
      },
      {
        heading: "Getting started on Duri",
        paragraphs: [
          "Set your home zone and office pin, filter for women-preferred if needed, and look for hosts with 10+ completed commute trips. Use the Hyderabad daily commute page for route-specific tips.",
        ],
      },
    ],
    relatedLinks: [
      { label: "Hyderabad daily commute", path: ROUTE_PATHS.HYDERABAD_DAILY_COMMUTE_CARPOOL },
      { label: "Hyderabad carpool hub", path: ROUTE_PATHS.HYDERABAD_CARPOOL },
      { label: "Daily commute carpool", path: ROUTE_PATHS.DAILY_COMMUTE_CARPOOL },
    ],
  },
  "intercity-ridesharing-vs-cab-bus": {
    slug: "intercity-ridesharing-vs-cab-bus",
    title: "Benefits of intercity ride sharing vs cab or bus",
    description:
      "Compare cost, comfort, timing, and flexibility of intercity carpools against private cabs and AC buses on busy Indian highway routes.",
    eyebrow: "Explainer",
    intro:
      "On routes like Hyderabad–Vijayawada (275 km, NH65), travellers usually choose between AC RTC/private bus, outstation cab, or carpool. Each option trades cost, comfort, and control differently.",
    sections: [
      {
        heading: "Cost: carpool wins for solo travellers",
        paragraphs: [
          "A private outstation cab can cost ₹4,000–₹5,500 one way. AC bus tickets run ₹700–₹900 but surge during festivals. Duri carpool seats on the same corridor often start around ₹450–₹550 — because fuel and tolls are split across four seats, not marked up as commercial fare.",
        ],
      },
      {
        heading: "Comfort: sedans vs bus seats",
        paragraphs: [
          "Buses are economical but fixed schedules, multiple stops, and 5+ hour journeys in full coaches wear people down. Carpools use sedans and SUVs with 3–4 passengers max — AC, direct highway routing, and a break stop when the group agrees.",
          "Cabs offer door-to-door privacy at a premium. Carpools sit in the middle: near door-to-door pickup zones (Hitech City, Benz Circle) without solo-cab pricing.",
        ],
      },
      {
        heading: "Flexibility & timing",
        paragraphs: [
          "Buses run fixed timetables — miss one and wait hours. Cabs are on-demand but expensive for peak festival weekends. Carpools on busy corridors have predictable windows (early morning and evening) with both directions listed, especially Fri–Sun.",
        ],
      },
      {
        heading: "When to pick each option",
        bullets: [
          "Bus: budget-first, flexible on time, travelling light.",
          "Cab: urgency, odd hours, or full privacy for family.",
          "Carpool: regular corridor travel, weekend home trips, festival seasons when buses sell out.",
        ],
      },
    ],
    relatedLinks: [
      { label: "Hyderabad → Vijayawada carpool", path: `${ROUTE_PATHS.INTERCITY_ROUTES}/hyderabad-to-vijayawada-carpool` },
      { label: "Popular rides", path: ROUTE_PATHS.POPULAR_ROUTES },
      { label: "Ridesharing benefits", path: ROUTE_PATHS.RIDESHARING_BENEFITS },
    ],
  },
};

export function getGuide(slug: string): GuideArticle | undefined {
  return GUIDE_ARTICLES[slug];
}

export const GUIDE_SLUGS = Object.keys(GUIDE_ARTICLES);
